import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Download, QrCode, Phone, Mail, Globe, Building2, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { LOGO_DARK_BG } from '../config/branding';

// Company email for all business cards
const COMPANY_EMAIL = 'info@dein-gutachten-direkt.de';

const PublicVisitenkartePage = () => {
  const { salespersonId } = useParams();
  const [loading, setLoading] = useState(true);
  const [cardData, setCardData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBusinessCard();
  }, [salespersonId]);

  const loadBusinessCard = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/salespeople/${salespersonId}/business-card`
      );
      
      if (!response.ok) {
        setError('Visitenkarte nicht gefunden');
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setCardData(data);
      }
    } catch (err) {
      setError('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const downloadVCard = () => {
    if (!cardData) return;
    
    const blob = new Blob([cardData.vcard_content], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = cardData.vcard_filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
          <h1 className="text-xl font-bold text-gray-900 mb-2">{error}</h1>
          <p className="text-gray-600">Die angeforderte Visitenkarte existiert nicht.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Business Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header with Logo */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-white text-center">
            <img 
              src={LOGO_DARK_BG} 
              alt="Dein Gutachten Direkt" 
              className="h-12 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold mb-1">{cardData?.name}</h1>
            <p className="opacity-90">Vertriebsmitarbeiter</p>
          </div>

          {/* Contact Info */}
          <div className="p-6 space-y-4">
            <a href={`mailto:${COMPANY_EMAIL}`} 
               className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <div className="w-10 h-10 bg-brand-green/10 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <div className="text-xs text-gray-500">E-Mail</div>
                <div className="text-gray-900">{COMPANY_EMAIL}</div>
              </div>
            </a>

            <a href="tel:+494101847116" 
               className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <div className="w-10 h-10 bg-brand-green/10 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Telefon</div>
                <div className="text-gray-900">+49 4101 847 11 60</div>
              </div>
            </a>

            <a href="https://www.dein-gutachten-direkt.de" target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors">
              <div className="w-10 h-10 bg-brand-green/10 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Website</div>
                <div className="text-gray-900">www.dein-gutachten-direkt.de</div>
              </div>
            </a>

            <div className="flex items-center gap-4 p-3 rounded-xl">
              <div className="w-10 h-10 bg-brand-green/10 rounded-lg flex items-center justify-center">
                <MapPin className="w-5 h-5 text-brand-green" />
              </div>
              <div>
                <div className="text-xs text-gray-500">Adresse</div>
                <div className="text-gray-900">Möwenstraße 2, 25462 Rellingen</div>
              </div>
            </div>
          </div>

          {/* QR Code */}
          {cardData?.qr_code_base64 && (
            <div className="px-6 pb-4 text-center">
              <div className="bg-gray-50 rounded-2xl p-4">
                <img 
                  src={`data:image/png;base64,${cardData.qr_code_base64}`}
                  alt="QR Code"
                  className="w-40 h-40 mx-auto"
                />
                <p className="text-xs text-gray-500 mt-2">QR-Code scannen zum Speichern</p>
              </div>
            </div>
          )}

          {/* Action Button */}
          <div className="p-6 pt-2">
            <Button
              onClick={downloadVCard}
              className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-4 text-lg"
            >
              <Download className="w-5 h-5 mr-2" />
              Kontakt speichern
            </Button>
          </div>

          {/* Footer */}
          <div className="bg-slate-900 text-white p-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Building2 className="w-4 h-4" />
              <span className="font-medium">Dein Gutachten Direkt GmbH</span>
            </div>
            <p className="text-xs text-gray-400">
              Verlässlich. Kompetent. Präzise.
            </p>
          </div>
        </div>

        {/* Powered by */}
        <p className="text-center text-xs text-gray-400 mt-4">
          Digitale Visitenkarte
        </p>
      </div>
    </div>
  );
};

export default PublicVisitenkartePage;
